import React from 'react';
import {
  Card,
  Page,
  Layout,
  Form,
  CalloutCard,
  FormLayout,
  TextField,
  PageActions,
  ChoiceList
} from '@shopify/polaris';
import { Helmet } from 'react-helmet';
import { CheckError } from '../../common/Helpers';
import { apiFetch as fetch } from '../../common/Helpers';

class WelcomeSurvey extends React.Component {

  lastStep = "step2call";

  constructor(props) {
    super(props)

    this.state = {
      step: "step1form",
      text: null,
      textError: "Please provide some information.",
      email: "",
      emailError: "Please provide a correct email address.",
      role: "",
      roleError: "Please make a selection.",
      tags: [],
      tagsError: "Please make a selection.",
      submitting: false,
      preferences: []
    };
  }

  getStep1Form = () => {

    return (<Form onSubmit={this.handleSubmit}>
      <Card>
        <Card.Section title="What is the biggest problem you face when managing tags?">
          <TextField
            placeholder="What problems/issues/inefficiencies would you like to resolve?"
            onChange={this.handleTextChange}
            multiline={6}
            error={this.state.textError}
            value={this.state.text}
          />
        </Card.Section>
        <Card.Section title="You are using Tag Genie as?">
          <FormLayout>
            <ChoiceList
              choices={[
                { label: 'Store owner', value: 'owner' },
                { label: 'Store technical support', value: 'technicalSupport' },
                { label: 'Consultant', value: 'consultant' },
                { label: 'Other', value: 'other' },
              ]}
              selected={this.state.role}
              error={this.state.roleError}
              onChange={this.handleChoiceListChange}
            />
          </FormLayout>
        </Card.Section>
        <Card.Section title="What type of tags you would like to manage?">
          <FormLayout>
            <ChoiceList
              allowMultiple
              choices={[
                { label: 'Product tags', value: 'product' },
                { label: 'Customer tags', value: 'customer' },
                { label: 'Order tags', value: 'order' },
              ]}
              selected={this.state.tags}
              error={this.state.tagsError}
              onChange={this.handleTagsChange}
            />
          </FormLayout>
        </Card.Section>
        <Card.Section title="Tag Genie Admin email">
          <FormLayout>
            <TextField
              value={this.state.email}
              onChange={this.handleEmailChange}
              type="email"
              error={this.state.emailError}
              helpText={
                <span>
                  We will use this email for important updates regarding your account. We will never share this email with a 3rd party.
                </span>
              }
            />
          </FormLayout>
        </Card.Section>
        <Card.Section title="And finally...">
          <FormLayout>
            <ChoiceList
              allowMultiple
              choices={[
                { label: 'I would like to receive daily tag reports for my store', value: 'Receive daily reports' },
                { label: 'I would like to schedule an onboarding call', value: 'Schedule onboarding call' },
                { label: 'I need help with setting up my tagging strategy', value: 'Needs help with tagging strategy' },
              ]}
              selected={this.state.preferences}
              error={this.state.tagsError}
              onChange={this.handlePreferencesChange}
            />
          </FormLayout>
        </Card.Section>
      </Card>
    </Form>);
  }

  getStep2Form = () => {

    return (
      <CalloutCard
        illustration=""
        primaryAction={{
          content: 'Schedule Call',
          url: 'https://calendly.com/taggenie/30min',
        }}
      >
        <p>We would love to have a quick call to better understand your needs and point you in the right direction as soon as possible.</p>
        <p>We can help with anything Shopify related and Tag Genie, of course.</p>
        <p><a href="https://calendly.com/taggenie/30min" target="_blank" rel='noreferrer'>
          <img src='https://taggeniemain.blob.core.windows.net/images/call.png' width="500px" alt=""></img>
        </a>
        </p>
      </CalloutCard>
    );
  }

  getStep3Form = () => {

    return (
      <CalloutCard

        illustration="https://taggeniemain.blob.core.windows.net/images/chat-bubble.png"
        primaryAction={{
          content: 'Chat with us',
          onAction: this.onChat
        }}
      >
        <p>We are one click away. If you have a question or a suggestion, please click the chat bubble in the bottom right corner of your window.</p>
        <p><img src='https://taggeniemain.blob.core.windows.net/images/chat-box.png' width="300px" alt=""></img>
        </p>
      </CalloutCard>
    );
  }

  onChat = () => {

    if (window.$crisp == null) {
      return;
    }

    window.$crisp.push(["do", "chat:open"]);
  }

  handleChoiceListChange = (selected) => {
    this.setState({
      "role": selected,
      "roleError": selected != null && selected !== "" ? null : "Please, make a selection"
    });
  }

  handleTagsChange = (selected) => {
    this.setState({
      "tags": selected,
      "tagsError": selected != null && selected !== "" ? null : "Please, make a selection"
    });
  }

  handlePreferencesChange = (selected) => {
    this.setState({
      "preferences": selected,
    });
  }

  getStepUI = () => {

    var ui = {
      title: "",
      subtitle: "",
      ui: null
    };

    if (this.state.step === "step1form") {
      ui.title = "Welcome to Tag Genie";
      ui.subtitle = "We will ask a few questions to better understand your needs and onboard you as quickly as possible";
      ui.ui = this.getStep1Form();
    }

    if (this.state.step === "step2call") {
      ui.title = "Schedule Onboarding/Intro Call";
      ui.subtitle = "Want to discuss anything Shopify related? Schedule a short call to get up to speed fast and discover how Tag Genie can save a lot of time and money";
      ui.ui = this.getStep2Form();
    }

    if (this.state.step === "step3support") {
      ui.title = "Online Support";
      ui.subtitle = "Having an issue or idea? Contact us directly on our online chat.";
      ui.ui = this.getStep3Form();
    }

    return ui;
  }

  render() {

    let canProceed = true;
    const ui = this.getStepUI();

    if (this.state.step === "step1form") {
      canProceed = this.state.roleError === null && this.state.emailError === null && this.state.textError === null;
    }

    let primaryAction = { content: 'Next', onAction: this.proceedToNextStep, disabled: !canProceed, loading: this.state.submitting };
    return (
      <div>
        <Helmet>
          <title>Tag Genie - Welcome</title>
        </Helmet>
        <Page title={ui.title}
          primaryAction={primaryAction}
          subtitle={ui.subtitle}>
          <Layout>
            <Layout.Section>
              {ui.ui}
            </Layout.Section>
          </Layout>
          <PageActions
            primaryAction={primaryAction}
          />
        </Page>
      </div>
    );
  }

  handleTextChange = (value) => {

    let error = null;
    if (value === null || value === '' || value === undefined) {
      error = "Please provide some information.";
    }

    this.setState({
      text: value,
      textError: error
    });
  }

  toggleHelpWithTagStrategy = () => {
    this.setState({
      helpWithTagStrategy: !this.state.helpWithTagStrategy
    });
  }

  toggleHelpWithTagOverhaul = () => {
    this.setState({
      helpWithTagOverhaul: !this.state.helpWithTagOverhaul
    });
  }

  toggleHelpWithTagAutomation = () => {
    this.setState({
      helpWithTagAutomation: !this.state.helpWithTagAutomation
    });
  }

  toggleHelpWithTagConsistency = () => {
    this.setState({
      helpWithTagConsistency: !this.state.helpWithTagConsistency
    });
  }

  handleEmailChange = (newValue) => {

    this.setState({
      email: newValue
    }, () => { this.validateEmailField(); });
  }

  validateEmailField = () => {
    var isValid = this.isEmailFieldValid(this.state.email);

    var emailError = isValid === true ? null : "Please provide a correct email address.";
    this.setState({
      emailError: emailError
    });

    return isValid;
  }

  isEmailFieldValid = (email) => {
    var re = /\S+@\S+\.\S+/;
    var isValid = re.test(email);

    return isValid;
  }

  proceedToNextStep = () => {

    if (this.state.step === "step1form") {
      this.submitSurvey();
    }

    if (this.state.step === "step2call") {
      this.setState({
        step: "step3support"
      });
    }

    if (this.state.step === "step3support") {
      this.props.onCompleteSurvey();
    }
  }

  handleSubmit = () => {
    //TODO: call later;
    var valid = this.validateEmailField();

    if (!valid) {
      return;
    }

  }

  submitSurvey = () => {

    this.setState({
      "submitting": true
    });

    fetch(`api/storeConnections/survey`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        answerProblem: this.state.text,
        answerRole: this.state.role.toString(),
        answerTagTypes: this.state.tags,
        answerEmail: this.state.email,
        answerPreferences: this.state.preferences,
      })
    })
      .then(CheckError)
      .then(() => {
        this.setState({
          "submitting": false,
          "step": "step2call"
        });
      })
      .catch((error) => { alert(error) });
  }

  componentDidMount() {
  }
}

export default WelcomeSurvey;